import { Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FormModule } from '@app/shared/components/form/form.module';
import { BadgeComponent, ButtonComponent, ToastService } from '@teorema/patron';
import { utils } from 'src/app/shared';
import { ProfilePipelineAccess } from 'src/app/models/user.model';
import { PipelineAccessOwner, PipelineAccessService } from './pipeline-access.service';

type Ability = 'can_read' | 'can_execute';

interface PipelineGroup {
  type: string;
  label: string;
  rows: ProfilePipelineAccess[];
}

/** Nome amigável dos tipos conhecidos; os demais aparecem como estão. */
const TYPE_LABELS: Record<string, string> = {
  'ticket-pipeline': 'Análises do chamado',
};
const NO_TYPE = '__none__';

/**
 * Acesso (leitura de resultados e execução) às pipelines de IA de um perfil ou usuário, agrupado
 * pelo tipo da pipeline. Salva à parte do formulário que o hospeda.
 */
@Component({
  selector: 'app-pipeline-access',
  standalone: true,
  imports: [FormsModule, FormModule, ButtonComponent, BadgeComponent],
  template: `
    @if (!ownerId()) {
      <div class="mt-4">Salve o cadastro para definir o acesso às pipelines.</div>
    } @else {
      @if (loading()) {
        <div>Carregando pipelines...</div>
      }
      @if (rows(); as list) {
        <div class="d-flex align-items-center justify-content-between mt-4 gap-3">
          <small class="text-muted">
            @if (owner() === 'user') {
              Regra do usuário vale no lugar da dos perfis: com alguma pipeline marcada aqui, ele só
              acessa as marcadas. Sem nenhuma marcada, valem os perfis dele.
            } @else {
              Com alguma pipeline marcada neste perfil, o usuário que o tem só acessa as pipelines
              marcadas nos perfis dele (somadas); as demais ficam negadas. Perfil sem nenhuma
              marcada não restringe. Usuário com regra própria ignora a do perfil.
            }
          </small>
          @if (showSaveButton()) {
            <patron-button (click)="save()" iconName="bi-check2" [loading]="loading()">
              Salvar pipelines
            </patron-button>
          }
        </div>
        <div class="card mt-3">
          <div class="card-body p-0">
            <table class="table m-0">
              <thead>
                <tr>
                  <th class="surface-200">Pipeline</th>
                  <th style="width: 110px" class="text-center surface-200">Leitura</th>
                  <th style="width: 110px" class="text-center surface-200">Execução</th>
                </tr>
              </thead>
              <thead>
                <tr>
                  <th class="surface-200">
                    <input
                      type="text"
                      class="input-sm form-control"
                      placeholder="Filtrar pipelines"
                      [ngModel]="filterText()"
                      (ngModelChange)="filterText.set($event)"
                      [ngModelOptions]="{ standalone: true }" />
                  </th>
                  <th style="width: 110px" class="text-center surface-200">
                    <patron-switch
                      [ngModel]="allSelected(list, 'can_read')"
                      (ngModelChange)="setAll(list, 'can_read', $event)"
                      [ngModelOptions]="{ standalone: true }">
                    </patron-switch>
                  </th>
                  <th style="width: 110px" class="text-center surface-200">
                    <patron-switch
                      [ngModel]="allSelected(list, 'can_execute')"
                      (ngModelChange)="setAll(list, 'can_execute', $event)"
                      [ngModelOptions]="{ standalone: true }">
                    </patron-switch>
                  </th>
                </tr>
              </thead>
              @for (group of groups(); track group.type) {
                <tbody class="mb-4">
                  <tr>
                    <td class="surface-100 pt-5">
                      <h6 class="mb-0">{{ group.label }}</h6>
                    </td>
                    <td class="surface-100 text-center pt-5">
                      <patron-switch
                        [ngModel]="allSelected(group.rows, 'can_read')"
                        (ngModelChange)="setAll(group.rows, 'can_read', $event)"
                        [ngModelOptions]="{ standalone: true }">
                      </patron-switch>
                    </td>
                    <td class="surface-100 text-center pt-5">
                      <patron-switch
                        [ngModel]="allSelected(group.rows, 'can_execute')"
                        (ngModelChange)="setAll(group.rows, 'can_execute', $event)"
                        [ngModelOptions]="{ standalone: true }">
                      </patron-switch>
                    </td>
                  </tr>
                  @for (row of group.rows; track row.pipeline_id) {
                    <tr>
                      <td>
                        <div>{{ row.name }}</div>
                        <patron-badge size="sm" color="primary" class="me-1">{{
                          row.slug
                        }}</patron-badge>
                        @if (!row.enabled) {
                          <patron-badge size="sm" color="secondary">Inativa</patron-badge>
                        }
                      </td>
                      <td class="text-center">
                        <patron-switch
                          [ngModel]="row.can_read"
                          (ngModelChange)="set(row, 'can_read', $event)"
                          [ngModelOptions]="{ standalone: true }">
                        </patron-switch>
                      </td>
                      <td class="text-center">
                        <patron-switch
                          [ngModel]="row.can_execute"
                          (ngModelChange)="set(row, 'can_execute', $event)"
                          [ngModelOptions]="{ standalone: true }">
                        </patron-switch>
                      </td>
                    </tr>
                  }
                </tbody>
              }
              @if (!groups().length) {
                <tbody>
                  <tr>
                    <td colspan="3" class="text-center text-muted">
                      {{
                        list.length
                          ? 'Nenhuma pipeline encontrada.'
                          : 'Nenhuma pipeline cadastrada.'
                      }}
                    </td>
                  </tr>
                </tbody>
              }
            </table>
          </div>
        </div>
      }
    }
  `,
})
export class PipelineAccessComponent {
  private readonly access = inject(PipelineAccessService);
  private readonly toast = inject(ToastService);

  readonly owner = input<PipelineAccessOwner>('profile');
  readonly ownerId = input<string | undefined>();
  /** Falso quando o formulário que hospeda salva as pipelines junto (via `saveIfDirty`). */
  readonly showSaveButton = input(true);

  readonly rows = signal<ProfilePipelineAccess[] | undefined>(undefined);
  readonly loading = signal(false);
  readonly filterText = signal('');
  /** Flags como carregadas/salvas por último; base para saber se há alteração pendente. */
  private readonly savedFlags = signal('');
  readonly dirty = computed(() => this.flagsOf(this.rows()) !== this.savedFlags());

  /** Pipelines agrupadas pelo tipo (sem tipo por último), já filtradas pelo texto. */
  readonly groups = computed<PipelineGroup[]>(() => {
    const filter = utils.normalize(this.filterText());
    const byType = new Map<string, ProfilePipelineAccess[]>();
    for (const row of this.rows() ?? []) {
      if (filter && !utils.normalize(`${row.name} ${row.slug}`).includes(filter)) continue;
      const type = row.type || NO_TYPE;
      byType.set(type, [...(byType.get(type) ?? []), row]);
    }
    return [...byType.entries()]
      .sort(([a], [b]) => (a === NO_TYPE ? 1 : b === NO_TYPE ? -1 : a.localeCompare(b)))
      .map(([type, rows]) => ({
        type,
        label: type === NO_TYPE ? 'Sem tipo' : (TYPE_LABELS[type] ?? type),
        rows,
      }));
  });

  constructor() {
    effect(() => {
      const owner = this.owner();
      const id = this.ownerId();
      untracked(() => this.load(owner, id));
    });
  }

  allSelected(rows: ProfilePipelineAccess[], ability: Ability): boolean {
    return rows.length > 0 && rows.every(row => row[ability]);
  }

  /** Executar exige ver: ligar executar liga ler; desligar ler desliga executar. */
  set(row: ProfilePipelineAccess, ability: Ability, value: boolean): void {
    this.setAll([row], ability, value);
  }

  setAll(rows: ProfilePipelineAccess[], ability: Ability, value: boolean): void {
    const targets = new Set(rows.map(row => row.pipeline_id));
    this.rows.update(list =>
      list?.map(row => {
        if (!targets.has(row.pipeline_id)) return row;
        const next = { ...row, [ability]: !!value };
        if (ability === 'can_execute' && next.can_execute) next.can_read = true;
        if (ability === 'can_read' && !next.can_read) next.can_execute = false;
        return next;
      })
    );
  }

  private async load(owner: PipelineAccessOwner, id?: string): Promise<void> {
    this.rows.set(undefined);
    this.savedFlags.set('');
    if (!id) return;

    this.loading.set(true);
    try {
      this.setRows(await this.access.list(owner, id));
    } catch {
      // Sem acesso ao módulo de IA a aba só fica vazia; o resto da tela segue funcionando.
      this.setRows([]);
    } finally {
      this.loading.set(false);
    }
  }

  async save(): Promise<boolean> {
    const id = this.ownerId();
    const rows = this.rows();
    if (!id || !rows) return true;

    this.loading.set(true);
    try {
      this.setRows(
        await this.access.save(
          this.owner(),
          id,
          rows.map(({ pipeline_id, can_read, can_execute }) => ({
            pipeline_id,
            can_read,
            can_execute,
          }))
        )
      );
      this.toast.show({ color: 'success', message: 'Salvo com sucesso' });
      return true;
    } catch (err: any) {
      if (!err?.messageShown)
        this.toast.show({ color: 'error', message: 'Houve um erro ao salvar as pipelines' });
      return false;
    } finally {
      this.loading.set(false);
    }
  }

  /** Salva só se houve alteração; `true` também quando não havia nada a salvar. */
  async saveIfDirty(): Promise<boolean> {
    return this.dirty() ? this.save() : true;
  }

  private setRows(rows: ProfilePipelineAccess[]): void {
    this.rows.set(rows);
    this.savedFlags.set(this.flagsOf(rows));
  }

  private flagsOf(rows?: ProfilePipelineAccess[]): string {
    return (rows ?? []).map(r => `${r.pipeline_id}:${+r.can_read}${+r.can_execute}`).join('|');
  }
}
